<script lang="ts" setup>
import {
  UICheckbox,
  UIForm,
  UIFormItem,
  UIInput,
  UIInputNumber,
  UIModal,
  UIModalContent,
  UIModalFooter,
  UIModalHeader,
  UISelect,
  UIContentWrap,
} from '@gohighlevel/ghl-ui'
import { PlusIcon } from '@gohighlevel/ghl-icons/24/outline'
import {
  ProductLineEvents,
  ProductTypeOptions,
  RichTextInputField,
  CustomUIFileUpload,
  ContentTypes,
} from '@gohighlevel/ghl-proposals-common'
import { useCurrency, useProducts } from '@/composition'
import { computed, onMounted, ref } from 'vue'
import { useProductListStore } from '@/store/productListStore'
import { usePayment } from '@/composition/usePayment'
import { useEmitter } from '@/composable/useEmitter'
import { useBuilderStore } from '@/store/builder'
import { useAppStore } from '@/store/app'
import { useI18n } from 'vue-i18n'

const builderStore = useBuilderStore()
const i18nLocale = useI18n()
const appStore = useAppStore()
const productListStore = useProductListStore()
const { t } = i18nLocale
const { createNewProduct, loading: productLoading } = useProducts()
const { getCurrencyDetails, fetchPaymentSettings } = usePayment()
const { getCurrency, getCurrencySymbol } = useCurrency()
const emit = defineEmits([
  ProductLineEvents.CLOSE_ADD_PRODUCT_MODAL,
  ProductLineEvents.OPEN_ADD_PRODUCT_MODAL,
  ProductLineEvents.ADD_NEW_PRODUCT_TO_PRODUCTS_LIST,
])

const eventBus = useEmitter()
const currencyOptions = ref<any>([])
const disableCurrencyDropdown = ref<boolean>(false)
const saveForLater = ref(false)
const formRef = ref()
const props = withDefaults(
  defineProps<{
    show: boolean
    disabled?: boolean
    loading?: boolean
    elementVersion: number
  }>(),
  {
    show: false,
    disabled: false,
    loading: false,
    elementVersion: 1,
  }
)
const formValue = ref({
  name: '',
  productType: '',
  priceName: '',
  amount: 0,
  currency: getCurrency(),
  description: '',
  image: '',
})

const resetFormValue = () => {
  formValue.value.name = ''
  formValue.value.productType = ''
  formValue.value.priceName = ''
  formValue.value.amount = 0
  formValue.value.currency = getCurrency()
  formValue.value.description = ''
  formValue.value.image = ''
}

const cancelCallback = () => {
  emit(ProductLineEvents.CLOSE_ADD_PRODUCT_MODAL)
  resetFormValue()
}

const createPayload = () => {
  const { name, priceName, amount, productType, currency, description, image } =
    formValue.value
  return {
    name,
    productType,
    description,
    taxCode: null,
    image,
    prices: [
      {
        name: priceName,
        type: 'one_time',
        amount: typeof amount === 'number' && !isNaN(amount) ? amount : 0,
        currency,
      },
    ],
  }
}

const handleFormSubmit = async () => {
  try {
    await formRef.value.getForm().validate()
    const payload = createPayload()
    //Consider during product creation if user have enabled tax inclusive in payment global settings
    const paymentSettings = await fetchPaymentSettings(
      appStore.document?.locationId
    )
    const isTaxIncludedGlobalSettingEnabled = paymentSettings?.taxInclusive
    if (saveForLater.value) {
      const response = await createNewProduct(payload)
      emit(ProductLineEvents.ADD_NEW_PRODUCT_TO_PRODUCTS_LIST, {
        ...response,
        ...(isTaxIncludedGlobalSettingEnabled && { taxInclusive: true }),
      })
    } else {
      emit(ProductLineEvents.ADD_NEW_PRODUCT_TO_PRODUCTS_LIST, {
        ...payload,
        ...(isTaxIncludedGlobalSettingEnabled && { taxInclusive: true }),
      })
    }
    emit(ProductLineEvents.CLOSE_ADD_PRODUCT_MODAL)
    resetFormValue()
  } catch (error) {
    console.error(error)
  }
}

const validationRules = {
  name: {
    required: true,
    trigger: 'blur',
    message: t('validations.required.productName'),
  },
  productType: {
    required: true,
    trigger: 'blur',
    message: t('validations.required.productType'),
  },
  priceName: {
    required: true,
    trigger: 'blur',
    message: t('validations.required.priceName'),
  },
  amount: {
    required: true,
    trigger: ['blur', 'input'],
    validator() {
      const v = formValue.value.amount
      if (
        v === null ||
        v === undefined ||
        typeof v !== 'number' ||
        isNaN(v) ||
        v < 0 ||
        (v.toString().includes('.') && v.toString().split('.')[1].length > 2)
      ) {
        return new Error(t('validations.invalid.validAmount'))
      }
    },
  },
  currency: {
    required: true,
    trigger: 'blur',
    message: t('validations.required.selectCurrency'),
  },
}

const setAllowChoosingCurrency = () => {
  if (productListStore.currency) {
    formValue.value.currency = productListStore.currency
    disableCurrencyDropdown.value = true
  } else {
    disableCurrencyDropdown.value = false
  }
}

const setCurrencyOptions = async () => {
  const options = productListStore?.validCurrencyOptions || []
  if (options && options.length > 0) {
    currencyOptions.value = productListStore?.validCurrencyOptions
  } else {
    await getCurrencyDetails()
    currencyOptions.value = productListStore?.validCurrencyOptions
  }
}

onMounted(() => {
  eventBus.on(ProductLineEvents.OPEN_ADD_PRODUCT_MODAL, () => {
    setCurrencyOptions()
    setAllowChoosingCurrency()
  })
})
const onDescriptionChange = (value: string) => {
  formValue.value.description = value
}
const amountChange = (value: number) => {
  if (value === 0) {
    saveForLater.value = false
  }
  formValue.value.amount = value
}

function onSelectImage(image: any) {
  if (!image) return
  formValue.value.image = image
}
function onDeleteImage() {
  formValue.value.image = ''
}

const hasInvalidDecimalPlaces = computed(() => {
  const v = formValue.value.amount
  if (
    v !== null &&
    v !== undefined &&
    typeof v === 'number' &&
    !isNaN(v) &&
    v.toString().includes('.')
  ) {
    const decimalPart = v.toString().split('.')[1]
    return decimalPart && decimalPart.length > 2
  }
  return false
})

const isSaveForLaterDisabled = computed(() => {
  return formValue.value.amount <= 0 || hasInvalidDecimalPlaces.value
})
</script>

<template>
  <UIModal :show="props.show" :width="920" class-name="add-new-product-modal">
    <template #header>
      <UIModalHeader
        id="modal-header"
        :icon="PlusIcon"
        :title="$t('common.newItem')"
        @close="cancelCallback"
      />
    </template>
    <UIModalContent>
      <UIForm
        id="add-new-product-form"
        ref="formRef"
        :model="formValue"
        :rules="validationRules"
      >
        <div class="w-6/12" v-if="props.elementVersion >= 3">
          <CustomUIFileUpload
            @select="onSelectImage"
            :image="formValue.image"
            @delete="onDeleteImage"
            :handshake="appStore.handshake"
            :company-id="appStore.companyId"
            :location-id="appStore.document?.locationId"
            :allowed-extensions="['image']"
            :content-type="ContentTypes.IMAGE"
          />
        </div>
        <UIContentWrap
          :locale="i18nLocale?.locale?.value || 'en-US'"
          class="p-0"
          style="display: contents"
        >
          <UIFormItem :label="$t('common.productName')" path="name" required>
            <UIInput
              id="product-name"
              v-model="formValue.name"
              :placeholder="$t('common.inputName')"
            />
          </UIFormItem>
        </UIContentWrap>
        <UIFormItem :label="$t('common.description')" path="description">
          <RichTextInputField
            min-height="300px"
            :editable="builderStore.isDocumentEditable"
            :value="formValue.description"
            @on-change="onDescriptionChange"
            to=".add-new-product-modal"
          />
        </UIFormItem>
        <UIFormItem
          :label="$t('common.productType')"
          path="productType"
          required
        >
          <UISelect
            id="product-drop-down"
            v-model:value="formValue.productType"
            :options="ProductTypeOptions"
            :placeholder="$t('common.selectProductType')"
          />
        </UIFormItem>
        <div class="flex">
          <div class="w-2/5 pr-4">
            <UIContentWrap
              :locale="i18nLocale?.locale?.value || 'en-US'"
              class="p-0"
              style="display: contents"
            >
              <UIFormItem
                :label="$t('common.priceName')"
                path="priceName"
                required
              >
                <UIInput
                  id="price-name"
                  v-model="formValue.priceName"
                  :placeholder="$t('common.priceName')"
                />
              </UIFormItem>
            </UIContentWrap>
          </div>
          <div class="w-2/5 pr-4">
            <UIFormItem :label="$t('common.amount')" path="amount" required>
              <UIInputNumber
                id="new-product-amount"
                :model-value="formValue.amount"
                @update:model-value="amountChange"
                :show-button="false"
                size="large"
                :min="0"
                :class="{ 'amount-strikethrough': hasInvalidDecimalPlaces }"
              >
                <template #prefix>{{
                  getCurrencySymbol(formValue.currency)
                }}</template>
              </UIInputNumber>
            </UIFormItem>
          </div>
          <div class="w-1/5">
            <UIFormItem :label="$t('common.currency')" path="currency" required>
              <UISelect
                id="change-product-currency"
                v-model:value="formValue.currency"
                :options="currencyOptions"
                :disabled="disableCurrencyDropdown"
                :placeholder="$t('common.selectCurrency')"
              />
            </UIFormItem>
          </div>
        </div>
      </UIForm>
    </UIModalContent>
    <template #footer>
      <UIModalFooter
        id="modal-footer"
        :disabled="productLoading"
        :loading="productLoading"
        :negative-text="$t('common.cancel')"
        :positive-text="$t('common.addNewItem')"
        @negativeClick="cancelCallback"
        @positiveClick="handleFormSubmit"
      >
        <template #actionLeft>
          <UICheckbox
            id="modal-form-checkout"
            v-model:checked="saveForLater"
            :disabled="!!isSaveForLaterDisabled"
          >
            {{ $t('common.saveForLaterUse') }}
          </UICheckbox>
        </template>
      </UIModalFooter>
    </template>
  </UIModal>
</template>

<style scoped>
:deep(.amount-strikethrough input) {
  text-decoration: line-through;
}
</style>
